import { DataDisplayTable } from "components/data-display";
import { StatusPanel } from 'components/status/StatusPanel';
import { Modal, ModalSize, NotificationInline, NotificationToaster } from "components/notifications";
import { ShowHide } from "components/layout";
import React, { useCallback, useState, useEffect, useMemo, Fragment } from 'react';
import { Tooltip } from "components/tooltip";
import { Button, TextButton } from "components/buttons";
import { TyreModelSpecs } from "pages/tyre-checks/modals/TyreModelSpecs";
import 'pages/tyre-checks/TyreCheck.css';
import classNames from 'classnames';
import { DateToLocal } from "components/dates";
import { Numeric } from "components/formatting";
import { DefectSignoffModal } from "components/Defect";
import { TyreCheckService } from 'services/TyreCheckService';
import { Intent } from "@blueprintjs/core";

export function TyreCard({ order, positionId }) {
    const [defectHeaderSections, setDefectHeaderSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showDefectSignoffModal, setShowDefectSignoffModal] = useState(false);
    const [hasDefects, setHasDefects] = useState(false);
    const [status, setStatus] = useState('none');
    const [signedOff, setSignedOff] = useState(false);
    const [position, setPostion] = useState(order.positions[positionId]);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [savingSignoff, setSavingSignoff] = useState(false);

    const [localDefect, setLocalDefect] = useState(null);

    const columnsPerRow = 4;

    const firstDamageIndex = position.damages && position.damages.length > 0 ? position.damages[0].damageId - 1 : null;

    const setUpDefectHeader = useCallback(() => {
        if (!position) return [];

        return [
            {
                header: "Brand",
                value: position.brand,
                key: "brand"
            },
            {
                header: "Model",
                value: position.model,
                key: "model"
            },
            {
                header: "Dimensions",
                value: position.dimension,
                key: "dimension"
            },

            {
                header: "Extra Details",
                value: (
                    <TextButton
                        onClick={() => setShowDetailsModal(true)}
                    >
                        Tyre model specs
                    </TextButton>
                ),
                key: "extra_details"
            },
            {
                header: "Serial Number",
                value: position.serialNumber,
                key: "serial_number"
            },
            {
                header: "DOT Code",
                value: position.dot,
                key: "dot_code"
            },
            {
                header: "Mileage",
                value: (
                    <Numeric>{position.mileage}</Numeric>
                ),
                key: "mileage"
            },
            {
                header: "Retreaded",
                value: position.isRetreaded ? "Yes" : "No",
                key: "retreaded"
            }, {
                header: "Regrooved",
                value: position.isRegrooved ? "Yes" : "No",
                key: "regrooved"
            },
            {
                header: "Tread Depth",
                value: position.treadDepth ? `${position.treadDepth} mm` : 'N/A',
                key: "tread_depth"
            },
            {
                header: "Pressure",
                value: position.pressure ? `${position.pressure} PSI` : 'N/A',
                key: "pressure"
            },
            {
                header: "Defects Raised",
                value: position.damages ? position.damages.length : 0,
                key: "defects-raised",
            },
        ];
    }, [position]);


    const setupPage = useCallback(() => {
        if (!position) return;

        setHasDefects(!!(position.damages && position.damages.length > 0));
        setSignedOff(!!position.signOffDateTime);

        setDefectHeaderSections(setUpDefectHeader());
        if (hasDefects && signedOff) {
            setStatus('resolved');
        } else if (hasDefects && !signedOff) {
            setStatus('unresolved');
        } else {
            setStatus('none');
        }
    }, [position, setUpDefectHeader, hasDefects, signedOff]);

    useEffect(() => {
        if (position) {
            setupPage();
        }
        setLoading(false);
    }, [position, setupPage]);


    function generateImageUrl(img) {
        return `${img?.storageName}/${img.containerName}/${img?.image}?${img?.token}`;
    }

    function openSignOffModal(defectId) {
        const defect = {
            defectId: defectId,
        };

        setLocalDefect(defect);
        setShowDefectSignoffModal(true);
    }

    function signOffDefect(defectSignOff) {
        setSavingSignoff(true);

        TyreCheckService.signOffDefect(defectSignOff.defectId, order.jobId, defectSignOff).then(
            (reponse) => {
                NotificationToaster.show(Intent.SUCCESS, "Defect signed off  successfully.");
                signOffCompleted(defectSignOff);
            })
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, "Failed to sign off defect. Please try again later.");
            })
            .finally(() => {
                setSavingSignoff(false);
            });
    }

    const images = useMemo(() => {
        if (!position?.images) return null;

        return (
            <div className="image-group">
                {position.images.map((img) => (
                    <Button
                        key={img.fileName}
                        onClick={() => {
                            setSelectedImage(generateImageUrl(img));
                            setShowImageModal(true);
                        }}
                        icon={<img src={generateImageUrl(img)} alt="" height={100} />}
                        minimal
                    />
                ))}
            </div>
        );
    }, [position]);

    function getStatus(damage) {
        if (damage.signOffDateTime) {
            return 'resolved';
        } else {
            return 'unresolved';
        }
    }

    function getTyrePositionDesc(tyrePosition) {
        switch (tyrePosition) {
            case "1":
                return 'N/S Outer';
            case "2":
                return 'N/S Inner';
            case "3":
                return 'O/S Inner';
            case "4":
                return 'O/S Outer';
            case "5":
                return 'Spare Wheel';
            default:
                return '';
        }
    }
    function signOffCompleted(updatedDefect) {
        setLocalDefect(updatedDefect);
        setShowDefectSignoffModal(false);
        setPostion((prev) => {

            var newPosition = { ...prev };

            const defectIndex = newPosition.damages.findIndex(d => d.damageId === updatedDefect.defectId);

            var newDamage = {
                ...prev.damages[defectIndex],
                signedOffBy: updatedDefect.signOffUser,
                signOffDateTime: updatedDefect.signOffDate,
                rectified: updatedDefect.rectified,
                rectifiedComment: updatedDefect.comment,
                maintenanceProvider: updatedDefect.maintenanceProvider,
                rectifiedBy: updatedDefect.rectifiedBy
            };

            newPosition.damages[defectIndex] = newDamage;
            return newPosition;
        });
    }

    return (
        <div>
            <StatusPanel title={`Axis ${position.axleNumber}, ${getTyrePositionDesc(position.tyrePosition)}`} status={status} loading={loading} >
                <DataDisplayTable
                    data={defectHeaderSections}
                    displayInRows={false}
                    loading={loading}
                    columnsPerRow={columnsPerRow}
                />
                <div className={classNames({ "bp6-skeleton": loading })}>
                    <h3 className="tire-task-h3">Comment</h3>
                    <span>
                        {position.comment ? (
                            <div>{position.comment}</div>
                        ) : (
                            'No comments.'
                        )}
                    </span>
                </div>
                <div className={classNames({ "bp6-skeleton": loading })}>
                    <h3 className="tire-task-h3">Media</h3>
                    <span>{position.images && position.images.length > 0 ? (
                        <div>
                            {images}
                        </div>
                    ) : 'No images.'}</span>
                </div>
                <div className={classNames('tyre-cards', { "bp6-skeleton": loading })}>
                    <span>{position.damages && position.damages.map((d) => (
                        <StatusPanel
                            title={`Reported Tyre Defect ${d.damageId - firstDamageIndex}`}
                            status={getStatus(d)}
                            loading={loading}
                            key={`defect-${d.damageId}`}>

                            <div className="defectSubHeader">
                                <div className={classNames({ "bp6-skeleton": loading })}>
                                    <h4>Defect Description</h4>
                                    <p>{d.damage}</p>
                                </div>
                            </div>
                            <ShowHide
                                evaluator={!d.signOffDateTime}
                                show={
                                    <div>
                                        <Tooltip content={"Sign off"}>
                                            <div className={classNames('defect-sign-off-section', { "bp6-skeleton": loading })}>
                                                <Button className={classNames({ "bp6-skeleton": loading })}
                                                    intent="primary"
                                                    text="SIGN OFF DEFECT"
                                                    id="button-sign-off"
                                                    loading={loading}
                                                    onClick={() => openSignOffModal(d.damageId)}
                                                    icon="tick"
                                                    large={false}
                                                    disabled={loading || d.signOff}
                                                />
                                            </div>
                                        </Tooltip>
                                    </div>
                                }
                                hide={
                                    <Fragment>
                                        <ShowHide
                                            evaluator={d.rectified}
                                            show={
                                                <Fragment>
                                                    <DataDisplayTable
                                                        data={[
                                                                {
                                                                    header: "Defect Rectified",
                                                                    value: 'Yes',
                                                                    key: 'rectified-description-' + d.damageId
                                                                },
                                                                {
                                                                    header: "Rectified Date",
                                                                    value: (
                                                                        <DateToLocal format='DD/MM/YYYY HH:mm'>{d.signOffDateTime}</DateToLocal>
                                                                    ),
                                                                    key: 'rectified-date' + d.damageId
                                                                },
                                                                {
                                                                    header: "Rectified By",
                                                                    value: d.signedOffBy,
                                                                    key: 'rectified-by' + d.damageId
                                                                }
                                                            ] 
                                                        }
                                                        displayInRows={false}
                                                        loading={loading}
                                                        columnsPerRow={3}
                                                    />  
                                                    <div className="defectSubHeader">
                                                        <div className={classNames({ "bp6-skeleton": loading })}>
                                                            <h4>Action Taken to Rectify the Defect</h4>
                                                            <p>{d.rectifiedComment}</p>
                                                        </div>
                                                    </div>
                                                </Fragment>
                                            }
                                            hide={
                                                <Fragment>
                                                    <div className="defectSubHeader">
                                                        <div className={classNames({ "bp6-skeleton": loading })}>
                                                            <h4>Defect Rectified</h4>
                                                            <p>No</p>
                                                        </div>
                                                    </div>
                                                    <div className="defectSubHeader">
                                                        <div className={classNames({ "bp6-skeleton": loading })}>
                                                            <h4>Reason Defect was not Rectified</h4>
                                                            <p>{d.rectifiedComment}</p>
                                                        </div>
                                                    </div>  
                                                </Fragment>
                                            }
                                        ></ShowHide>
  

                                        <ShowHide
                                            evaluator={d.signOffDateTime}
                                            show={
                                                <NotificationInline
                                                    allowClose={false}
                                                    show
                                                    text={<div>Defect signed off by {d.signedOffBy} on <DateToLocal format='DD/MM/YYYY HH:mm'>{d.signOffDateTime}</DateToLocal></div>}
                                                    intent="success"
                                                ></NotificationInline>  
                                            }
                                        />
                                    </Fragment>

                                }
                            ></ShowHide>

                        </StatusPanel>
                    ))}</span>
                </div>
            </StatusPanel>
            <Modal
                isOpen={showImageModal}
                onClose={() => setShowImageModal(false)}
                size={ModalSize.SMALL}
                showCloseButton={true}
            >
                <img alt="" src={selectedImage} style={{ width: '100%' }} />
            </Modal>

            <DefectSignoffModal
                title="Sign Off Defect"
                isOpen={showDefectSignoffModal}
                loading={loading}
                defect={localDefect}
                onClose={() => setShowDefectSignoffModal(false)}
                signOffDefect={signOffDefect}
                saving={savingSignoff}
                UserAccessActions={["Fleet:View", "FleetPCN:View", "FleetVIS:View"]}
            />

            <TyreModelSpecs loading={loading} position={position} isOpen={showDetailsModal} onClose={() => setShowDetailsModal(false)} />
        </div >
    );
}